import { type ReactNode } from "react";
import { clsx } from "clsx";

export interface AppShellLayoutProps {
  topNav: ReactNode;
  // Compose <Sidebar> inside this slot — it owns its own <aside> width + mobile drawer.
  sideNav?: ReactNode;
  // P1: right-side inspector slot. Composed by shell, primitive owns its own <aside>.
  rightSlot?: ReactNode;
  // P1: edge reveal tab rendered as fixed-positioned sibling of the shell wrapper.
  rightEdgeTab?: ReactNode;
  // Row 5.3 (mobile <768 nav): optional bottom nav slot. Additive/backward-compatible — omitted
  // (the default) renders nothing, so existing consumers are unaffected. Composed by the shell;
  // the primitive owns its own visibility-at-mobile-tier logic, same pattern as sideNav/rightSlot.
  bottomTabBar?: ReactNode;
  // Row 1.7 — optional global footer. Same additive/backward-compatible pattern as bottomTabBar:
  // omitted (the default) renders nothing. A persistent thin bar (not page content), consistent
  // with this shell's fixed-viewport (h-screen) layout — not a scrolling page-end footer.
  footer?: ReactNode;
  // App focus/Max (CFR-VW-145): hide the top header (and, by the consumer passing sideNav=undefined,
  // the sidebar) for a near-full-screen app. Additive/backward-compatible — omitted (the default)
  // renders the header exactly as before, so existing consumers are unaffected.
  hideChrome?: boolean;
  children: ReactNode;
  className?: string;
}

export function AppShellLayout({ topNav, sideNav, rightSlot, rightEdgeTab, bottomTabBar, footer, hideChrome, children, className }: AppShellLayoutProps) {
  // DS depth model (spec 21 phase 2): page canvas = surface-base (graphite #191A1B);
  // raised chrome (header/sidebar/cards) steps up to elevated/subtle.
  return (
    <div className={clsx("flex flex-col h-screen w-full bg-[var(--color-surface-base)]", className)}>
      {!hideChrome && (
        <header className="flex-shrink-0 h-12 border-b border-[var(--color-border-default)] bg-[var(--surface-sunken)]">
          {topNav}
        </header>
      )}
      <div className="flex flex-1 overflow-hidden">
        {sideNav}
        <main className="flex-1 overflow-hidden bg-[var(--color-surface-base)]">{children}</main>
        {rightSlot}
      </div>
      {footer}
      {bottomTabBar}
      {rightEdgeTab}
    </div>
  );
}
